import React, { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DEFAULT_CUSTOMIZATION = {
  logo_url: "",
  background_image_url: "",
  background_blur: false,
  background_opacity: 100,
  primary_color: "#3B82F6",
  secondary_color: "#1E40AF",
  foundation_year: null,
  club_description: "",
  website: "",
  instagram: "",
  facebook: "",
  season_banner_text: "",
  season_banner_visible: false
};

const PersonalizzaClub = ({ token, club, onSave, showMsg }) => {
  const [customization, setCustomization] = useState(DEFAULT_CUSTOMIZATION);
  const [originalData, setOriginalData] = useState(DEFAULT_CUSTOMIZATION);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);

  useEffect(() => {
    loadCustomization();
  }, []);

  useEffect(() => {
    const changed = JSON.stringify(customization) !== JSON.stringify(originalData);
    setHasChanges(changed);
  }, [customization, originalData]);

  const loadCustomization = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clubs/customization`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomization(data);
        setOriginalData(data);
      }
    } catch (e) {
      console.error('Error loading customization:', e);
    }
    setLoading(false);
  };

  const handleImageUpload = async (file, imageType) => {
    if (!file) return;
    
    const setUploading = imageType === 'logo' ? setUploadingLogo : setUploadingBg;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const response = await fetch(`${BACKEND_URL}/api/clubs/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const urlField = imageType === 'logo' ? 'logo_url' : 'background_image_url';
        setCustomization(prev => ({ ...prev, [urlField]: data.url }));
        showMsg('success', `${imageType === 'logo' ? 'Logo' : 'Sfondo'} caricato! Compressione: ${data.compression_ratio}%`);
      } else {
        const err = await response.json();
        showMsg('error', err.detail || 'Errore upload');
      }
    } catch (e) {
      showMsg('error', 'Errore durante l\'upload');
    }
    setUploading(false);
  };

  const saveCustomization = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clubs/customization`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customization)
      });
      
      if (response.ok) {
        setOriginalData(customization);
        showMsg('success', 'Personalizzazione salvata!');
        if (onSave) onSave(customization);
      } else {
        showMsg('error', 'Errore nel salvataggio');
      }
    } catch (e) {
      showMsg('error', 'Errore di connessione');
    }
    setSaving(false);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Sei sicuro di voler ripristinare tutte le impostazioni ai valori predefiniti?')) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clubs/customization/reset`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setCustomization(DEFAULT_CUSTOMIZATION);
        setOriginalData(DEFAULT_CUSTOMIZATION);
        showMsg('success', 'Personalizzazione ripristinata ai valori predefiniti');
        if (onSave) onSave(DEFAULT_CUSTOMIZATION);
      } else {
        showMsg('error', 'Errore nel ripristino');
      }
    } catch (e) {
      showMsg('error', 'Errore di connessione');
    }
    setSaving(false);
  };

  const removeImage = (imageType) => {
    const urlField = imageType === 'logo' ? 'logo_url' : 'background_image_url';
    setCustomization(prev => ({ ...prev, [urlField]: '' }));
  };

  // Build full URL for images
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">🎨</span> Personalizza Club
            </h2>
            <p className="text-gray-500 text-sm mt-1">Personalizza l'aspetto del tuo club</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetToDefaults}
              disabled={saving}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              data-testid="reset-customization-btn"
            >
              Ripristina default
            </button>
            <button
              onClick={saveCustomization}
              disabled={saving || !hasChanges}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasChanges 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="save-customization-btn"
            >
              {saving ? '⏳ Salvataggio...' : '💾 Salva modifiche'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-bold text-gray-700">👁️ Anteprima in tempo reale</h3>
        </div>
        <div 
          className="relative h-48 flex items-center justify-center"
          style={{
            backgroundColor: customization.primary_color,
            backgroundImage: customization.background_image_url 
              ? `url(${getFullImageUrl(customization.background_image_url)})` 
              : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="preview-header"
        >
          {/* Blur overlay */}
          {customization.background_image_url && customization.background_blur && (
            <div 
              className="absolute inset-0" 
              style={{ 
                backdropFilter: 'blur(8px)',
                backgroundColor: `rgba(0,0,0,${(100 - customization.background_opacity) / 100})`
              }}
            />
          )}
          {/* Opacity overlay (when no blur) */}
          {customization.background_image_url && !customization.background_blur && customization.background_opacity < 100 && (
            <div 
              className="absolute inset-0" 
              style={{ 
                backgroundColor: `rgba(0,0,0,${(100 - customization.background_opacity) / 100})`
              }}
            />
          )}
          {/* Content */}
          <div className="relative z-10 flex items-center gap-4">
            {customization.logo_url ? (
              <img 
                src={getFullImageUrl(customization.logo_url)} 
                alt="Logo club"
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                data-testid="preview-logo"
              />
            ) : (
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-3xl border-4 border-white shadow-lg"
                style={{ backgroundColor: customization.secondary_color }}
              >
                🏢
              </div>
            )}
            <div className="text-white text-shadow">
              <h2 className="text-2xl font-bold drop-shadow-lg">{club?.name || 'Nome Club'}</h2>
              <p className="text-sm opacity-90">{club?.city || 'Città'}</p>
              {customization.foundation_year && (
                <p className="text-xs opacity-75">Fondato nel {customization.foundation_year}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Banner Preview */}
        {customization.season_banner_visible && customization.season_banner_text && (
          <div 
            className="px-4 py-2 text-center text-white text-sm font-medium"
            style={{ backgroundColor: customization.secondary_color }}
            data-testid="preview-banner"
          >
            {customization.season_banner_text}
          </div>
        )}
      </div>

      {/* Settings Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        
        {/* 1. LOGO */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🖼️</span> Logo del Club
          </h3>
          <p className="text-sm text-gray-500 mb-4">JPG/PNG, max 2MB. Compresso automaticamente.</p>
          
          <div className="flex items-center gap-4">
            {customization.logo_url ? (
              <div className="relative">
                <img 
                  src={getFullImageUrl(customization.logo_url)} 
                  alt="Logo"
                  className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                />
                <button
                  onClick={() => removeImage('logo')}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                  title="Rimuovi logo"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                <span className="text-3xl">🏢</span>
              </div>
            )}
            
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
              className="hidden"
            />
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="upload-logo-btn"
            >
              {uploadingLogo ? '⏳ Caricamento...' : '📤 Carica Logo'}
            </button>
          </div>
        </div>

        {/* 2. SFONDO */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🌄</span> Immagine di Sfondo
          </h3>
          <p className="text-sm text-gray-500 mb-4">JPG/PNG, max 5MB. Sfondo header pagine.</p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {customization.background_image_url ? (
                <div className="relative">
                  <img 
                    src={getFullImageUrl(customization.background_image_url)} 
                    alt="Sfondo"
                    className="w-32 h-20 rounded-lg object-cover border-2 border-gray-200"
                  />
                  <button
                    onClick={() => removeImage('background')}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    title="Rimuovi sfondo"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="w-32 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                  <span className="text-2xl">🌄</span>
                </div>
              )}
              
              <input
                ref={bgInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleImageUpload(e.target.files[0], 'background')}
                className="hidden"
              />
              <button
                onClick={() => bgInputRef.current?.click()}
                disabled={uploadingBg}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50"
                data-testid="upload-background-btn"
              >
                {uploadingBg ? '⏳ Caricamento...' : '📤 Carica Sfondo'}
              </button>
            </div>
            
            {customization.background_image_url && (
              <div className="space-y-3 pt-3 border-t">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customization.background_blur}
                    onChange={(e) => setCustomization(prev => ({ ...prev, background_blur: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded"
                    data-testid="background-blur-toggle"
                  />
                  <span className="text-sm">Effetto blur</span>
                </label>
                
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">
                    Opacità: {customization.background_opacity}%
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={customization.background_opacity}
                    onChange={(e) => setCustomization(prev => ({ ...prev, background_opacity: parseInt(e.target.value) }))}
                    className="w-full"
                    data-testid="background-opacity-slider"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 3. COLORI */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🎨</span> Colori del Club
          </h3>
          <p className="text-sm text-gray-500 mb-4">Sostituiscono il blu default nell'app.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Colore Primario</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={customization.primary_color}
                  onChange={(e) => setCustomization(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                  data-testid="primary-color-picker"
                />
                <input
                  type="text"
                  value={customization.primary_color}
                  onChange={(e) => setCustomization(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                  placeholder="#3B82F6"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Colore Secondario</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={customization.secondary_color}
                  onChange={(e) => setCustomization(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                  data-testid="secondary-color-picker"
                />
                <input
                  type="text"
                  value={customization.secondary_color}
                  onChange={(e) => setCustomization(prev => ({ ...prev, secondary_color: e.target.value }))}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                  placeholder="#1E40AF"
                />
              </div>
            </div>
          </div>
          
          {/* Color Presets */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-gray-500 mb-2">Preset rapidi:</p>
            <div className="flex flex-wrap gap-2">
              {[
                { p: '#3B82F6', s: '#1E40AF', name: 'Blu' },
                { p: '#10B981', s: '#047857', name: 'Verde' },
                { p: '#EF4444', s: '#B91C1C', name: 'Rosso' },
                { p: '#8B5CF6', s: '#6D28D9', name: 'Viola' },
                { p: '#F59E0B', s: '#D97706', name: 'Arancio' },
                { p: '#EC4899', s: '#BE185D', name: 'Rosa' },
                { p: '#1F2937', s: '#111827', name: 'Nero' }
              ].map(preset => (
                <button
                  key={preset.name}
                  onClick={() => setCustomization(prev => ({ ...prev, primary_color: preset.p, secondary_color: preset.s }))}
                  className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-50 text-xs"
                  title={preset.name}
                >
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.p }}></div>
                  <span>{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 4. INFO CLUB */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📋</span> Info Club
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Anno di fondazione</label>
              <input
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={customization.foundation_year || ''}
                onChange={(e) => setCustomization(prev => ({ 
                  ...prev, 
                  foundation_year: e.target.value ? parseInt(e.target.value) : null 
                }))}
                placeholder="Es: 1985"
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="foundation-year-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Descrizione <span className="text-gray-400 font-normal">({(customization.club_description || '').length}/200)</span>
              </label>
              <textarea
                value={customization.club_description || ''}
                onChange={(e) => setCustomization(prev => ({ 
                  ...prev, 
                  club_description: e.target.value.slice(0, 200) 
                }))}
                placeholder="Breve descrizione del club..."
                className="w-full px-3 py-2 border rounded-lg resize-none"
                rows="2"
                data-testid="club-description-input"
              />
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">🌐 Sito web</label>
                <input
                  type="url"
                  value={customization.website || ''}
                  onChange={(e) => setCustomization(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://www.example.com"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="website-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">📸 Instagram</label>
                <input
                  type="text"
                  value={customization.instagram || ''}
                  onChange={(e) => setCustomization(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="@username"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="instagram-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">📘 Facebook</label>
                <input
                  type="text"
                  value={customization.facebook || ''}
                  onChange={(e) => setCustomization(prev => ({ ...prev, facebook: e.target.value }))}
                  placeholder="Nome pagina o URL"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="facebook-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 5. BANNER STAGIONE - Full width */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:col-span-2">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>📣</span> Banner Stagione
          </h3>
          <p className="text-sm text-gray-500 mb-4">Messaggio personalizzato in cima alla Dashboard.</p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customization.season_banner_visible}
                  onChange={(e) => setCustomization(prev => ({ ...prev, season_banner_visible: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded"
                  data-testid="banner-visible-toggle"
                />
                <span className="text-sm font-medium">Mostra banner</span>
              </label>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Testo del banner</label>
              <input
                type="text"
                value={customization.season_banner_text || ''}
                onChange={(e) => setCustomization(prev => ({ ...prev, season_banner_text: e.target.value }))}
                placeholder="Es: Stagione 2025/26 — Forza Green! 💚"
                className="w-full px-3 py-2 border rounded-lg"
                disabled={!customization.season_banner_visible}
                data-testid="banner-text-input"
              />
            </div>
            
            {/* Banner Preview */}
            {customization.season_banner_visible && customization.season_banner_text && (
              <div 
                className="px-4 py-2 rounded-lg text-center text-white text-sm font-medium"
                style={{ backgroundColor: customization.secondary_color }}
              >
                {customization.season_banner_text}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save/Reset Footer */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm text-gray-500">
            {hasChanges ? (
              <span className="text-orange-600 font-medium">⚠️ Modifiche non salvate</span>
            ) : (
              <span className="text-green-600">✅ Tutto salvato</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={resetToDefaults}
              disabled={saving}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
            >
              🔄 Ripristina default
            </button>
            <button
              onClick={saveCustomization}
              disabled={saving || !hasChanges}
              className={`px-6 py-2 rounded-lg text-sm font-medium ${
                hasChanges 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? '⏳ Salvataggio...' : '💾 Salva modifiche'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizzaClub;
